import { FC } from 'react';
import {
  useGridApiContext,
  useGridSelector,
  GridPagination,
  gridRowCountSelector,
  gridPageSizeSelector,
} from '@mui/x-data-grid';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Box, Stack, Theme } from '@mui/material';
import Icon from '@/components/icon';
import { getNiceNumber } from '@/lib/helpers';
import Loader from '@/components/loader';
import FlatButton from '@/components/flat-button';
import Tooltip from '@/components/tooltip';
import { DtGridApiCommunity } from '../helpers';
import PaginationAction from './pagination-action';
import CountLabel from './count-label';

const useStyles = createUseStyles((theme: Theme) => ({
  gridPagination: {
    '& .MuiPagination-root': {
      marginLeft: 8,
    },
    '&.loading': {
      opacity: 0.75,
      pointerEvents: 'none',
    },
    '& .MuiToolbar-root': {
      paddingLeft: 16,
    },
    '& .MuiTablePagination-input': {
      marginRight: 0,
      '& .MuiTablePagination-select': {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  liveButton: {
    height: 24,
    width: 66,
    margin: 0,
    fontSize: theme.typography.caption.fontSize,
    minHeight: '0 !important',

    '& svg': {
      height: 16,
      width: 16,
      marginRight: -8,
    },
  },
}));

interface Props {
  page: number;
  count: number;
  onExport?: () => void;
  exportTooltip?: string;
  isLoadingExport: boolean;
  isLoading: boolean;
  isCursorMode: boolean;
  enablePolling: boolean;
  onPollingChange: () => void;
  countLabel?: string;
  countLoading?: boolean;
}

const Pagination: FC<Props> = (props) => {
  const styles = useStyles();

  const {
    page,
    count,
    isLoadingExport,
    onExport,
    exportTooltip = '',
    isLoading,
    isCursorMode,
    enablePolling,
    onPollingChange,
    countLabel = '',
    countLoading,
  } = props;

  const apiRef = useGridApiContext();
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const hasExport = !!onExport;

  const handleRefresh = () => {
    (apiRef.current as DtGridApiCommunity).refresh();
  };

  const start = page * pageSize + 1;
  const end = Math.min(start + pageSize - 1, rowCount);
  const hasRows = !!count;
  const compIsLoading = isLoading || isLoadingExport;
  const compClass = classNames(styles.gridPagination, { loading: compIsLoading });
  const hasCountLabel = !!countLabel;

  return (
    <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%">
      <Stack direction="row" justifyContent="flex-start" alignItems="center" flexGrow={1} gap={1}>
        {!isCursorMode && (
          <Icon
            color="active"
            name="refresh"
            onClick={handleRefresh}
            spinOnClick
            disabled={compIsLoading}
          />
        )}

        {hasExport && (
          <>
            <Tooltip title={exportTooltip} disabled={!exportTooltip}>
              <FlatButton
                icon="export"
                label="EXPORT"
                onClick={onExport}
                color="link"
                size="small"
                disabled={compIsLoading}
              />
            </Tooltip>
          </>
        )}
      </Stack>
      {hasRows && (
        <>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" flexGrow={1} gap={1}>
            {hasCountLabel ? (
              <CountLabel label={countLabel} isLoading={countLoading} />
            ) : (
              <>
                {compIsLoading ? (
                  <Box px={1} width="100px">
                    <Loader />
                  </Box>
                ) : (
                  <Box>
                    {getNiceNumber(start)} - {getNiceNumber(end)} of {getNiceNumber(rowCount)}
                  </Box>
                )}
              </>
            )}
            <GridPagination
              ActionsComponent={(props) => (
                <PaginationAction
                  {...props}
                  isCursorMode={isCursorMode}
                  enablePolling={enablePolling}
                  onPollingChange={onPollingChange}
                />
              )}
              className={compClass}
              labelRowsPerPage="Page Size:"
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default Pagination;
